//@ts-strict-ignore
import { DocumentNode, OperationVariables, QueryHookOptions, useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import { AllViews, useView } from '../../utils/useView';
import { Direction, IFilterInput, Maybe } from '../schema/p4types';
import { EntityType } from '../../@types/entityTypes';
import { ICustomColumn } from '../../utils/setColumnsFromArray';
import { useViews } from './useViews';
import { IWorkspaceColumnOverride } from '../../@types/workspaceTypes';
import { Paths } from '../../@types/keyOfNested';
import { useMemo } from 'react';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import lodash from 'lodash';

export type WorkspaceConfig<Entity = any> = {
  viewScreenName: string; //Unique identifier for this view.
  altUserId?: number;
  location?: any; //Current location, e.g. props.location or 'useLocation()' result.
  //Should use additionalOptions.filters over this if possible.
  predefinedFilters?: Array<IFilterInput>;
  embeddedView?: boolean; //Is this workspace (view) embedded inside another form/modal, or is it standalone.
  altView?: any;
  additionalVariables?: any;
  pageSize?: number;
  //Should use additionalOptions.initialSortColumn over this if possible.
  initialSortColumn?: keyof Entity | string;
  //Should use additionalOptions.initialSortDirection over this if possible.
  initialSortDir?: Direction;
  //Props needed for view initialization
  columnOverrides?: IWorkspaceColumnOverride; //Would prefer to deprecate this - Use AdditionalOptions instead.
  dashboard?: boolean;
  miniGrid?: boolean;
  hasDynamicColumns?: boolean;
  availableColumns?: Array<any> | undefined;
  defaultQuery?: DocumentNode;
  defaultTotalColumns?: Array<string>;
};

type AdditionalOptions<Entity = any, IWorkspaceQueryResult = any, IWorkspaceQueryVariables = any> = {
  screen?: string;
  filters?: Array<IFilterInput>;
  additionalQueryOptions?: QueryHookOptions<any, any>;
  columns: ReadonlyArray<Paths<Entity>>;
  columnOverrides?: IWorkspaceColumnOverride;
  customColumns?: Maybe<{ [key: string]: ICustomColumn }>;
  initialSortColumn: keyof Entity | string;
  initialSortDirection?: Direction;
  deferViewInitialize?: boolean; //Do not initialize views if this is true, e.g. because fieldNames haven't been loaded yet.
  additionalVariables?: Partial<IWorkspaceQueryVariables>;
};

const useWorkspaceStore = () => {
  const focusArea = useSelector((store: any) => store.app.focusArea);
  const autoFocus = useSelector((store: any) => store.app.autoFocus);
  const actionEntities = useSelector((store: any) => store.app.actionEntities);
  const expandedRows = useSelector((store: any) => store.app.expandedRows);
  return { focusArea, autoFocus, actionEntities, expandedRows };
};

export const mergeCustomizerArrayConcat = (l: any, r: any) => {
  //Default merge is to overwrite where index is the same - But in this case we want the arrays to simply be appended together.
  if (Array.isArray(l) && Array.isArray(r)) {
    return [...new Set([...l, ...r])];
  }
};

/**
 * Workspace Query Hook.
 * Includes the Workspace Query, View Query & View Mutations.
 * Notes:
 *  - filters as a function is deprecated. Store/ConnectedForm/etc is accessible with hooks by functional components.
 *  - customVariables as a function is deprecated. useParam() hook can cover all existing uses with additionalVariables.
 */
export const useWorkspace = <
  Entity,
  IWorkspaceQueryResult = any,
  IWorkspaceQueryVariables extends OperationVariables = any
>(
  {
    viewScreenName: viewScreenNameFromProps,
    altUserId,
    location,
    predefinedFilters = [],
    embeddedView,
    altView: altViewFromProps,
    additionalVariables: additionalVariablesFromProps, //Would prefer to deprecate, use 'AdditionalOptions.additionalVariables' instead.
    pageSize = 30,
    initialSortDir: initialSortDirectionFromProps,
    //Props needed for view initialization:
    columnOverrides: columnOverridesFromProps, //Would prefer to deprecate, use AdditionalOptions instead.
    dashboard,
    miniGrid,
    hasDynamicColumns,
    defaultQuery,
    defaultTotalColumns,
  }: WorkspaceConfig<Entity>,
  { workspace: query, type }: Pick<EntityType, 'workspace' | 'type'>, //Entity
  {
    screen,
    filters,
    additionalQueryOptions = {},
    columns = [],
    columnOverrides,
    customColumns, //e.g attribute columns, free stock columns, etc
    initialSortColumn,
    initialSortDirection,
    deferViewInitialize,
    additionalVariables,
  }: AdditionalOptions<Entity, IWorkspaceQueryResult, IWorkspaceQueryVariables> //Now mandatory - Columns needs to always be passed through when using 'useWorkspace' due to changes to view generation.
) => {
  //Shouldn't really be necessary (The components that need these values should access them directly), but makes migration easier as Workspace.js will expect these in props.
  const workspaceStore = useWorkspaceStore();
  const views = useSelector((store: { views: AllViews }) => store.views); //Need to do views separately to mapStateToWorkspaceProps or they won't trigger a render on change.
  const dispatch = useDispatch(); //Workspaces are expecting dispatch to be passed in via props, might as well pass it from here.
  const mergedOverrides = useMemo(
    () => ({ ...(columnOverridesFromProps ?? {}), ...(columnOverrides ?? {}) }),
    [columnOverrides, columnOverridesFromProps]
  );
  const viewProps = useViews<Entity>({
    viewScreenName: viewScreenNameFromProps,
    altUserId,
    columnOverrides: mergedOverrides,
    initialSortColumn: initialSortColumn,
    initialSortDir: initialSortDirection || initialSortDirectionFromProps,
    dashboard,
    columns,
    miniGrid,
    hasDynamicColumns,
    defaultQuery,
    entity: { workspace: query, type },
    embeddedView,
    additionalVariables,
    customColumns,
    deferViewInitialize,
    defaultTotalColumns,
  });

  const {
    filters: viewFilters,
    orderDirection,
    orderBy,
    viewColumns,
    totalColumns,
    skip,
    currentView,
  } = useView<Entity>(
    {
      viewScreenName: viewScreenNameFromProps,
      location,
      predefinedFilters,
      views,
      altView: altViewFromProps,
      initialSortColumn,
      initialSortDir: initialSortDirection || initialSortDirectionFromProps,
    },
    screen,
    filters
  );

  const aggregateCriteriaColumnOverrides = useMemo(
    () =>
      viewProps?.availableColumns
        ?.filter((availableColumn) => {
          return availableColumn.totalCalculation;
        })
        ?.map(({ fieldName, totalCalculation }) => ({ fieldName, totalCalculation })),
    [viewProps?.availableColumns]
  );

  const includeFlags = useMemo(() => {
    const newIncludeFlags: { [key: `include${string}${string}`]: boolean } = {};
    viewColumns?.forEach((column) => {
      //Just supporting up to a depth or 2.
      const fieldNames = column.fieldName
        ?.replace(/\[\d+]/g, '') // Remove [0], [1], etc.
        .split('.')
        .map((subField) => subField.charAt(0).toUpperCase() + subField.slice(1));
      newIncludeFlags[`include${fieldNames[0]}`] = true;
      newIncludeFlags[`include${fieldNames[0]}${fieldNames[1]}`] = true;
    });
    return newIncludeFlags;
  }, [viewColumns]);

  const variables = useMemo(() => {
    const baseVariables = {
      first: pageSize,
      after: 0,
      orderBy: orderBy,
      orderDirection: orderDirection,
      filters: viewFilters,
      embeddedView: embeddedView,
      aggregateCriteria: {
        sumColumns: totalColumns,
        columnOverrides: aggregateCriteriaColumnOverrides,
      },
    };
    return lodash.mergeWith(
      {},
      baseVariables,
      additionalVariablesFromProps || {},
      additionalVariables || {},
      includeFlags,
      mergeCustomizerArrayConcat
    );
  }, [
    additionalVariables,
    additionalVariablesFromProps,
    aggregateCriteriaColumnOverrides,
    embeddedView,
    includeFlags,
    orderBy,
    orderDirection,
    pageSize,
    totalColumns,
    viewFilters,
  ]);

  const handleQueryError = useComponentQueryErrorHandling();

  /*
   * We ignore the 'views not loaded' skip criteria here if 'deferViewInitialize' is set, because the places where we use that
   * (Primarily 'global amendments') we use some metadata from the workspace query to generate the fieldNames, but we use the
   * fieldNames to drive 'deferring' on view initialization - An impossible loop, so this resolves that.
   * (Note - If/when global amendments ever get a rewrite/update, we should review this behaviour).
   */

  const appUser = useSelector((store: any) => store.app.user);
  const querySkip = !appUser || (!deferViewInitialize && (viewProps?.storedViews?.loading || skip));

  const { error, loading, fetchMore, refetch, data } = useQuery<IWorkspaceQueryResult, IWorkspaceQueryVariables>(
    query as DocumentNode,
    {
      variables,
      fetchPolicy: querySkip ? 'cache-only' : 'cache-and-network',
      notifyOnNetworkStatusChange: true,
      skip: querySkip,
      onError: handleQueryError,
      ...additionalQueryOptions,
    }
  );

  const { additionalData, listData, list } = useMemo(() => data || ({} as any), [data]);

  return useMemo(
    () => ({
      hookBasedWorkspace: true,
      ...viewProps,
      ...workspaceStore,
      views,
      dispatch,
      error,
      loading,
      additionalData,
      listData: listData || list,
      workspaceFilters: viewFilters, //Return the filters being used to simplify 'getActionProps' calls so we don't have to keep retrieving them from the view.
      columnOverrides,
      customColumns,
      loadMoreRows: (index: number, replaceData: boolean = false) => {
        const baseVariables = {
          first: pageSize,
          after: index,
          orderBy: orderBy,
          orderDirection: orderDirection,
          filters: viewFilters,
          embeddedView: embeddedView,
          aggregateCriteria: {
            sumColumns: totalColumns,
            columnOverrides: aggregateCriteriaColumnOverrides,
          },
        } as unknown as IWorkspaceQueryVariables;
        const variables = lodash.mergeWith(
          {},
          baseVariables,
          additionalVariablesFromProps || {},
          additionalVariables || {},
          includeFlags,
          mergeCustomizerArrayConcat
        );
        if (replaceData) {
          //Refetch will reset the cache
          return refetch(variables);
        }
        //Fetchmore will merge into the cache
        return fetchMore({
          query: query,
          variables: variables,
        });
      },
      currentView,
      initialSortColumn,
    }),
    [
      additionalData,
      additionalVariables,
      additionalVariablesFromProps,
      aggregateCriteriaColumnOverrides,
      columnOverrides,
      currentView,
      customColumns,
      dispatch,
      embeddedView,
      error,
      fetchMore,
      includeFlags,
      initialSortColumn,
      list,
      listData,
      loading,
      orderBy,
      orderDirection,
      pageSize,
      query,
      refetch,
      totalColumns,
      viewFilters,
      viewProps,
      views,
      workspaceStore,
    ]
  );
};
